import { useState, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { Building2, ChevronDown, Check } from 'lucide-react';

const OrgSwitcher: React.FC = () => {
    const { accessibleOrganizations, activeOrganizationUuid, switchOrganization } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [switching, setSwitching] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Hide if user has access to fewer than 2 orgs
    if (accessibleOrganizations.length < 2) {
        return null;
    }

    const activeOrg = accessibleOrganizations.find(
        (org) => org.organizationUuid === activeOrganizationUuid
    );

    const handleSwitch = async (orgUuid: string) => {
        if (orgUuid === activeOrganizationUuid || switching) return;
        setSwitching(true);
        try {
            await switchOrganization(orgUuid);
            setIsOpen(false);
        } catch {
            // Error already handled by AuthContext (toast)
        } finally {
            setSwitching(false);
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                disabled={switching}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-sm"
                aria-label="Switch organization"
                data-testid="org-switcher-trigger"
            >
                <Building2 className="w-4 h-4 text-indigo-600" />
                <span className="max-w-[160px] truncate text-gray-700 font-medium">
                    {switching ? 'Switching...' : (activeOrg?.organizationName ?? 'Select Org')}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div
                    className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
                    data-testid="org-switcher-dropdown"
                >
                    <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                        Organizations
                    </div>
                    {accessibleOrganizations.map((org) => {
                        const isActive = org.organizationUuid === activeOrganizationUuid;
                        return (
                            <button
                                key={org.organizationUuid}
                                onClick={() => handleSwitch(org.organizationUuid)}
                                disabled={switching}
                                className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between gap-2 transition-colors ${
                                    isActive
                                        ? 'bg-indigo-50 text-indigo-700 font-medium'
                                        : 'text-gray-700 hover:bg-gray-50'
                                }`}
                                data-testid={`org-option-${org.organizationUuid}`}
                            >
                                <span className="truncate">{org.organizationName}</span>
                                {isActive && <Check className="w-4 h-4 text-indigo-600 flex-shrink-0" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default OrgSwitcher;
